import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { normalizeCotwVisibility, sanitizeCotwLinks } from '../lib/cotwVisibility';

const DEFAULT_CONFIG = {
  navigation: {
    links: [
      { label: 'Tournaments', path: '/tournaments' },
      { label: 'News', path: '/news' },
      { label: 'Servers', path: '/servers' },
      { label: 'Players', path: '/users' },
    ],
  },
  // Site publication is separate from the worker/event enabled flag.
  compOfTheWeek: { published: false },
  hero: {
    tagline: 'Competitive tournaments for the KZ community.',
    primaryCta: { label: 'View Tournaments', path: '/tournaments' },
    secondaryCta: { label: 'Watch Live', path: '/stream' },
  },
  landing: {
    hero: {
      dossierPrefix: 'DOSSIER · KZCOMP',
      coordinates: 'N 40° 01′ 31″ / W 105° 16′ 16″',
      headlineLine1: 'High altitude',
      headlineLine2: 'competition.',
      backgroundImageUrl: '',
      backgroundStoragePath: '',
    },
    fieldReport: {
      eyebrow: '— Field Report',
      seasonLabel: 'Season',
      liveLabel: 'Live now',
      upcomingLabel: 'Upcoming',
      broadcastLabel: 'Broadcast active',
    },
    ledger: {
      enabled: true,
      eyebrow: 'Operations ledger',
      title: 'Active & upcoming',
      viewAllLabel: 'All tournaments',
      viewAllPath: '/tournaments',
      featuredTournamentId: '',
      featuredBadge: 'Featured',
    },
    mapOfDay: {
      eyebrow: 'Minigame · Daily drop',
      title: 'Map of the day',
      description:
        'Six guesses. Letter-by-letter feedback. Unlock hints to reveal the mapper. Resets at midnight.',
      ctaLabel: 'Play today',
      ctaPath: '/map-guessing',
    },
    youtube: {
      enabled: false,
      eyebrow: 'Broadcast · YouTube',
      title: 'Watch on YouTube',
      description: 'Highlights, full VODs, and tournament casts from the KZcomp channel.',
      ctaLabel: 'Open channel',
      ctaPath: '',
    },
    community: {
      eyebrow: 'Join the expedition',
      headlineLine1: 'Bring your',
      headlineLine2: 'best time.',
      description:
        'KZCOMP runs seasonal tournaments, community events, and guessing games. Join the community or watch from the stands.',
      discordLabel: 'Discord',
      browseLabel: 'Browse players',
      browsePath: '/users',
    },
  },
  staff: {
    description:
      'KZCOMP is built and run by these people. Everyone listed here has contributed real work to the platform or its events — development, tournament administration, mapping, or community support.',
  },
  footer: {
    tagline: 'Competitive tournaments for the KZ community.',
    columns: [
      {
        heading: 'Tournaments',
        links: [
          { label: 'Browse tournaments', path: '/tournaments' },
          { label: 'Leaderboards', path: '/tournaments' },
          { label: 'Rules', path: '/rules' },
        ],
      },
      {
        heading: 'Community',
        links: [
          { label: 'Players', path: '/users' },
          { label: 'Stream', path: '/stream' },
        ],
      },
      {
        heading: 'About',
        links: [
          { label: 'KZcomp', path: '/' },
          { label: 'Contact', path: '/contact' },
        ],
      },
    ],
    socials: [
      { platform: 'discord', url: '' },
      { platform: 'twitch', url: '' },
      { platform: 'youtube', url: '' },
    ],
    copyright: 'KZcomp.com',
  },
};

const SiteConfigContext = createContext({ config: DEFAULT_CONFIG, loading: true });

const merge = (base, override) => {
  if (override === undefined || override === null) return base;
  if (Array.isArray(override)) return override;
  if (typeof override !== 'object') return override;
  if (typeof base !== 'object' || base === null) return override;
  const out = { ...base };
  for (const key of Object.keys(override)) {
    out[key] = merge(base[key], override[key]);
  }
  return out;
};

const SITE_ORIGIN = 'https://kzcomp.com';
const SITE_HOSTS = new Set(['kzcomp.com', 'www.kzcomp.com']);

const legacyMediaPathname = (value) => {
  if (typeof value !== 'string') return '';

  const raw = value.trim();
  if (!raw) return '';

  const isAbsolute = /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(raw);
  if (!isAbsolute && !raw.startsWith('/')) return '';

  try {
    const parsed = new URL(raw, SITE_ORIGIN);
    if (
      isAbsolute &&
      (!/^https?:$/i.test(parsed.protocol) || !SITE_HOSTS.has(parsed.hostname.toLowerCase()))
    ) {
      return '';
    }

    return decodeURIComponent(parsed.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return '';
  }
};

export const isLegacyMediaPath = (path) => legacyMediaPathname(path) === '/media';

export const removeLegacyMediaLinks = (links) =>
  (Array.isArray(links) ? links : []).filter((link) => !isLegacyMediaPath(link?.path));

// Firestore arrays replace their defaults during merge, so stale links need to
// be removed from the merged result as well as from DEFAULT_CONFIG.
export const sanitizeConfig = (config) => {
  const source = config && typeof config === 'object' ? config : {};
  const navigation = source.navigation || {};
  const footer = source.footer || {};
  const cotwPublished = normalizeCotwVisibility(source);

  return {
    ...source,
    compOfTheWeek: {
      ...(source.compOfTheWeek || {}),
      published: cotwPublished,
    },
    navigation: {
      ...navigation,
      links: sanitizeCotwLinks(removeLegacyMediaLinks(navigation.links), cotwPublished),
    },
    footer: {
      ...footer,
      columns: Array.isArray(footer.columns)
        ? footer.columns.map((column) =>
            column
              ? {
                  ...column,
                  links: sanitizeCotwLinks(removeLegacyMediaLinks(column.links), false),
                }
              : column
          )
        : footer.columns,
    },
  };
};

export const SiteConfigProvider = ({ children }) => {
  const [remote, setRemote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ref = doc(db, 'siteConfig', 'main');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setRemote(snap.exists() ? snap.data() : null);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return unsub;
  }, []);

  const value = useMemo(
    () => ({ config: sanitizeConfig(merge(DEFAULT_CONFIG, remote)), loading }),
    [remote, loading]
  );

  return <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>;
};

export const useSiteConfig = () => useContext(SiteConfigContext);
